import { Inject, Injectable } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { asyncScheduler, Observable, scheduled } from 'rxjs';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import * as ApplicationActions from './+state/application.actions';
import { ApplicationFacade } from './+state/application.facade';
import { MEDIA_QUERY_SIZE, MediaQuerySizeType } from './+state/application.models';
import { EnvironmentUpdateEnum } from '@ap-angular/shared/environment';
import { AbstractComponent } from '@ap-angular/shared/abstract';
import { MediaQueryLimit } from '@ap-angular/shared/schema';
import { EnvironmentUtils } from '@ap-angular/shared/utils';
import { EnvironmentService } from '@ap-angular/service/environment';
import { Platform } from '@angular/cdk/platform';

@Injectable()
export class ApplicationService extends AbstractComponent {

	//private isInitialized = false;

	private _listener = undefined;

	constructor(
		@Inject('ENVIRONMENT') private readonly _environment: any,
		private readonly _applicationFacade: ApplicationFacade,
		private readonly _eventManager: EventManager,
		private readonly _environmentService: EnvironmentService,
    private readonly _platform: Platform
	) {
		super();
	}

	//public init() {
	//	if (!this.isInitialized) {
	//		this.isInitialized = true;
	//	}
	//}

	public setMediaQuerySize(value: MediaQuerySizeType) {
		this._applicationFacade.dispatch(ApplicationActions.MediaQuerySizeChanged({mediaQuerySize: value}));
	}

	public setAppLanguage(value: string) {
		this._applicationFacade.dispatch(ApplicationActions.AppLanguage({language: value}));
	}

	public getMediaQuerySize$(): Observable<MediaQuerySizeType> {
		return this._applicationFacade.screen$;
	}

  public isMobilePlatform(): boolean {
    return this._platform.ANDROID || this._platform.IOS;
  }

	public getShowSearch$(): Observable<boolean> {
		return this._applicationFacade.showSearch$;
	}

	public getPositionOfSearchInput$(): Observable<number> {
		return this._applicationFacade.positionOfSearchInput$;
	}

	public setVersion(version: string, updated: EnvironmentUpdateEnum) {
		this._applicationFacade.dispatch(ApplicationActions.VersionAction({
			payload: {version: version, versionUpdate: updated}
		}));
	}

	public setShowSearch(showSearch: boolean) {
		this._applicationFacade.dispatch(ApplicationActions.ShowSearch({showSearch}));
	}

	public setPositionOfSearchInput(positionOfSearchInput: number) {
		this._applicationFacade.dispatch(ApplicationActions.PositionOfSearchInput({positionOfSearchInput}));
	}

	public getVersionUpdateRequired$(): Observable<number> {
		return this._applicationFacade.update$;
	}

	public initVersion() {
		super.addSubscription(this._environmentService.interval$()
			.pipe(
				filter(config => typeof config !== undefined),
				withLatestFrom(this._applicationFacade.version$)
			)
			.subscribe(([config, version]) => {
				if (version) {
					if (config.version !== version) {
						switch (EnvironmentUtils.getVersionUpdate(config, version)) {
							case EnvironmentUpdateEnum.MAJOR:
								this.setVersion(version, EnvironmentUpdateEnum.MAJOR);
								break;
							case EnvironmentUpdateEnum.MINOR:
								this.setVersion(version, EnvironmentUpdateEnum.MINOR);
								break;
							case EnvironmentUpdateEnum.PATH:
								this.setVersion(version, EnvironmentUpdateEnum.PATH);
								break;
						}
					}
				} else {
					this.setVersion(config.version, undefined);
				}
			})
		);
	}

	/** RESIZE section */
	public initResize() {
		this._windowdDestroy();
		this.setMediaQuerySize(this._getMediaQuerySize(window));
		this._listener = this._eventManager.addGlobalEventListener('window', 'resize', (event) => this.onResizeListener(event));
	}

	private _windowdDestroy() {
		if (this._listener) {
			// remove event listener
			this._listener();
		}
		this._listener = undefined;
	}

	private _getMediaQuerySize(window: Window): MediaQuerySizeType {
		const width = window.innerWidth;
		return width <= MediaQueryLimit.SMALL ? MEDIA_QUERY_SIZE.SMALL :
			width <= MediaQueryLimit.MEDIUM ? MEDIA_QUERY_SIZE.MEDIUM :
				width <= MediaQueryLimit.LARGE ? MEDIA_QUERY_SIZE.LARGE : MEDIA_QUERY_SIZE.EXTRA_LARGE;
	}

	private onResizeListener(event: UIEvent) {
		let currentDimension;
		this.getMediaQuerySize$()
			.pipe(take(1))
			.subscribe((next) => currentDimension = next)
			.unsubscribe();
		const newDimension = this._getMediaQuerySize(event.target as Window);
		if (currentDimension !== newDimension) {
			this.setMediaQuerySize(newDimension);
		}
	}

	public clearState$(): Observable<boolean> {
		this._applicationFacade.dispatch(ApplicationActions.ClearState());
		return scheduled([true], asyncScheduler);
	}
}
